import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { quizApi } from "@/lib/quizApi";
import { loadSession, saveSession } from "@/lib/session";
import { BrandHeader } from "@/components/BrandHeader";

const ID_TYPES = ["Aadhaar", "Passport", "Pan", "Other"] as const;
type IdType = typeof ID_TYPES[number];

export default function IdUpload() {
  const { quizId = "" } = useParams();
  const navigate = useNavigate();
  const session = loadSession();

  const [selfie, setSelfie] = useState<File | null>(null);
  const [idDoc, setIdDoc] = useState<File | null>(null);
  const [idType, setIdType] = useState<IdType>("Aadhaar");
  const [error, setError] = useState<string | null>(null);

  const uploadFlow = useMutation({
    mutationFn: async () => {
      if (!selfie || !idDoc) throw new Error("Please choose both files.");
      // 1) Create the attempt to get an attemptId
      const attempt = await quizApi.createAttempt(quizId);
      saveSession({ ...(session!), attemptId: attempt.attemptId });
      // 2) Upload the documents
      await quizApi.uploadIdDocument(attempt.attemptId, selfie, idDoc, idType);
      return attempt.attemptId;
    },
    onSuccess: () => navigate(`/quiz/${quizId}/instructions`),
    onError: (e: any) => setError(e?.response?.data?.detail ?? e?.message ?? "Upload failed."),
  });

  if (!session) return <Centered>Session expired. Please register again.</Centered>;

  return (
    <>
      <BrandHeader />
      <main className="mx-auto max-w-2xl p-6">
      <div className="card p-8">
        <h1 className="text-2xl font-bold text-brand-ink">Verify your identity</h1>
        <p className="mt-1 text-sm text-brand-ink/60">
          Upload a photo of yourself and a government ID. An admin will review after your exam.
        </p>

        <div className="mt-6 grid gap-5">
          <div>
            <label className="field-label">
              Your photo (selfie)<span className="ml-1 text-brand" aria-hidden>*</span>
            </label>
            <input type="file" accept="image/*" required aria-required="true"
              onChange={(e) => setSelfie(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm" />
          </div>
          <div>
            <label className="field-label">
              ID type<span className="ml-1 text-brand" aria-hidden>*</span>
            </label>
            <select className="field-input bg-transparent"
              value={idType} onChange={(e) => setIdType(e.target.value as IdType)}>
              {ID_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">
              Photo/scan of ID<span className="ml-1 text-brand" aria-hidden>*</span>
            </label>
            <input type="file" accept="image/*,application/pdf" required aria-required="true"
              onChange={(e) => setIdDoc(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm" />
          </div>
        </div>

        {error && <div className="mt-4 text-sm text-brand">{error}</div>}

        <div className="mt-6">
          <button className="btn-primary" disabled={!selfie || !idDoc || uploadFlow.isPending}
            onClick={() => uploadFlow.mutate()}>
            {uploadFlow.isPending ? "Uploading…" : "Continue →"}
          </button>
        </div>
      </div>
      </main>
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="grid min-h-screen place-items-center p-6 text-brand-ink/60">{children}</main>;
}
